import { computed, ref } from 'vue'

import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type { MultiselectSearchOption } from '@shared/ui/fields'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { TasksKanbanFilterParams } from './interfaces'

import { useFilterDateRange, useFilterSearch } from '@shared/lib/filters'

import { buildQueryObject, parseFilterParams } from './filters/parse-filter-params'
import { useFilterAssignee } from './filters/filter-assignee.hook'
import { useFilterTaskType } from './filters/filter-task-type.hook'
import { useFilterPriority } from './filters/filter-priority.hook'
import { useFilterActivationType } from './filters/filter-activation-type.hook'
import { useFilterAlertType } from './filters/filter-alert-type.hook'
import { useFilterOngoing } from './filters/filter-ongoing.hook'
import { useFilterSort } from './filters/filter-sort.hook'

interface TasksKanbanFiltersReturn {
  filterResetKey: Ref<number>
  searchQuery: Ref<string>
  deadlineRange: WritableComputedRef<Date[] | null>
  resolvedAtRange: WritableComputedRef<Date[] | null>
  assigneeOptions: ComputedRef<MultiselectSearchOption[]>
  assigneeInitialValue: ComputedRef<string[]>
  handleAssigneeSelected: (selected: MultiselectSearchOption[]) => void
  taskTypeOptions: ComputedRef<MultiselectSearchOption[]>
  taskTypeInitialValue: ComputedRef<string[]>
  handleTaskTypeSelected: (selected: MultiselectSearchOption[]) => void
  priorityOptions: ComputedRef<MultiselectSearchOption[]>
  priorityInitialValue: ComputedRef<string[]>
  handlePrioritySelected: (selected: MultiselectSearchOption[]) => void
  activationTypeOptions: ComputedRef<MultiselectSearchOption[]>
  activationTypeInitialValue: ComputedRef<string[]>
  handleActivationTypeSelected: (selected: MultiselectSearchOption[]) => void
  alertTypeOptions: ComputedRef<MultiselectSearchOption[]>
  alertTypeInitialValue: ComputedRef<string[]>
  handleAlertTypeSelected: (selected: MultiselectSearchOption[]) => void
  ongoingOptions: AppSelectOption[]
  ongoingValue: WritableComputedRef<string | undefined>
  sortOptions: AppSelectOption[]
  sortValue: WritableComputedRef<string | undefined>
  handleSearch: () => void
  handleClear: () => void
}

export const useTasksKanbanFilters = (): TasksKanbanFiltersReturn => {
  const route = useRoute()
  const router = useRouter()

  const filterResetKey = ref(0)

  const initialParams = parseFilterParams(route.query)

  const {
    query: searchQuery,
  } = useFilterSearch({ initial: initialParams.query })

  const {
    range: deadlineRange,
    from: deadlineFrom,
    to: deadlineTo,
  } = useFilterDateRange({
    initialFrom: initialParams.deadline_from,
    initialTo: initialParams.deadline_to,
  })

  const {
    range: resolvedAtRange,
    from: resolvedAtFrom,
    to: resolvedAtTo,
  } = useFilterDateRange({
    initialFrom: initialParams.resolved_at_from,
    initialTo: initialParams.resolved_at_to,
  })

  const {
    options: assigneeOptions,
    initialValue: assigneeInitialValue,
    handleSelected: handleAssigneeSelected,
    query: assigneeQuery,
  } = useFilterAssignee({ initial: initialParams.assignee_id })

  const {
    options: taskTypeOptions,
    initialValue: taskTypeInitialValue,
    handleSelected: handleTaskTypeSelected,
    query: taskTypeQuery,
  } = useFilterTaskType({ initial: initialParams.task_type })

  const {
    options: priorityOptions,
    initialValue: priorityInitialValue,
    handleSelected: handlePrioritySelected,
    query: priorityQuery,
  } = useFilterPriority({ initial: initialParams.priority })

  const {
    options: activationTypeOptions,
    initialValue: activationTypeInitialValue,
    handleSelected: handleActivationTypeSelected,
    query: activationTypeQuery,
  } = useFilterActivationType({ initial: initialParams.activation_type })

  const {
    options: alertTypeOptions,
    initialValue: alertTypeInitialValue,
    handleSelected: handleAlertTypeSelected,
    query: alertTypeQuery,
  } = useFilterAlertType({ initial: initialParams.alert_type })

  const {
    options: ongoingOptions,
    model: ongoingValue,
    query: ongoingQuery,
  } = useFilterOngoing({ initial: initialParams.is_new })

  const {
    options: sortOptions,
    model: sortValue,
    sortBy,
    sortDirection,
  } = useFilterSort({
    initialSortBy: initialParams.sort_by,
    initialSortDirection: initialParams.sort_direction,
  })

  const queryParams = computed<TasksKanbanFilterParams>(() => {
    const current = parseFilterParams(route.query)

    return {
      query: searchQuery.value,
      deadline_from: deadlineFrom.value,
      deadline_to: deadlineTo.value,
      resolved_at_from: resolvedAtFrom.value,
      resolved_at_to: resolvedAtTo.value,
      assignee_id: assigneeQuery.value,
      task_type: taskTypeQuery.value,
      priority: priorityQuery.value,
      activation_type: activationTypeQuery.value,
      alert_type: alertTypeQuery.value,
      is_new: ongoingQuery.value,
      sort_by: sortBy.value,
      sort_direction: sortDirection.value,
      customer_id: current.customer_id,
      related_to_type: current.related_to_type,
      related_to_id: current.related_to_id,
    }
  })

  const handleSearch = (): void => {
    const query = buildQueryObject(queryParams.value)
    void router.push({ query })
  }

  const handleClear = (): void => {
    searchQuery.value = ''
    deadlineRange.value = null
    resolvedAtRange.value = null
    assigneeQuery.value = []
    taskTypeQuery.value = []
    priorityQuery.value = []
    activationTypeQuery.value = []
    alertTypeQuery.value = []
    ongoingValue.value = undefined
    sortValue.value = undefined
    filterResetKey.value++

    void router.push({ query: {} })
  }

  return {
    filterResetKey,
    searchQuery,
    deadlineRange,
    resolvedAtRange,
    assigneeOptions,
    assigneeInitialValue,
    handleAssigneeSelected,
    taskTypeOptions,
    taskTypeInitialValue,
    handleTaskTypeSelected,
    priorityOptions,
    priorityInitialValue,
    handlePrioritySelected,
    activationTypeOptions,
    activationTypeInitialValue,
    handleActivationTypeSelected,
    alertTypeOptions,
    alertTypeInitialValue,
    handleAlertTypeSelected,
    ongoingOptions,
    ongoingValue,
    sortOptions,
    sortValue,
    handleSearch,
    handleClear,
  }
}
